import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { GooglePlacesAutocomplete } from '../components/ui/google-places-autocomplete';
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  MoreHorizontal,
  Mountain,
  Phone,
  MapPin,
  DollarSign,
  Package,
  ChevronRight,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { getStatesForCountry, getCountryByCode } from '../utils/countryStates';

const TipsMaterials = () => {
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tenantCountry, setTenantCountry] = useState('AU');
  
  // State
  const [yards, setYards] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingYard, setEditingYard] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'yard'|'material', yard?, index?, label }
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    type: 'tip',
    street_address: '',
    suburb: '',
    postcode: '',
    state: '',
    contact_name: '',
    contact_phone: '',
    notes: '',
    materials: [],
    is_active: true
  });
  
  // Drawer state for viewing/editing materials
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedYard, setSelectedYard] = useState(null);
  
  // Material pricing dialog
  const [materialDialogOpen, setMaterialDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [materialForm, setMaterialForm] = useState({
    material_type_id: '',
    unit: 'tonne',
    charge_rate: '',
    cost_rate: '',
    buyback_rate: '',
    subbie_rate: '',
    account_id: ''
  });

  useEffect(() => {
    fetchYards();
    fetchMaterialTypes();
    api.get('/tenants/settings').then(res => {
      setTenantCountry(res.data.country || 'AU');
    }).catch(() => {});
  }, [api]);

  const statesList = getStatesForCountry(tenantCountry);
  const countryMeta = getCountryByCode(tenantCountry);

  const fetchYards = async () => {
    try {
      const response = await api.get('/material-yards');
      setYards(response.data);
    } catch (error) {
      console.error('Failed to fetch yards', error);
      toast.error('Failed to load tips & materials');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterialTypes = async () => {
    try {
      const response = await api.get('/material-types?active_only=true');
      setMaterialTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch material types', error);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      type: 'tip',
      street_address: '',
      suburb: '',
      postcode: '',
      state: '',
      contact_name: '',
      contact_phone: '',
      notes: '',
      materials: [],
      is_active: true
    });
    setEditingYard(null);
  };

  const handleOpenDialog = (yard = null) => {
    if (yard) {
      setEditingYard(yard);
      setForm({
        name: yard.name,
        type: yard.type,
        street_address: yard.street_address || '',
        suburb: yard.suburb || '',
        postcode: yard.postcode || '',
        state: yard.state || '',
        contact_name: yard.contact_name || '',
        contact_phone: yard.contact_phone || '',
        notes: yard.notes || '',
        materials: yard.materials || [],
        is_active: yard.is_active
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingYard) {
        await api.put(`/material-yards/${editingYard.id}`, form);
        toast.success('Yard updated successfully');
      } else {
        await api.post('/material-yards', form);
        toast.success('Yard created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchYards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (yard) => {
    setDeleteConfirm({ type: 'yard', yard, label: yard.name });
  };

  const handleDeleteMaterialConfirm = (index) => {
    const mat = selectedYard?.materials?.[index];
    const typeMatch = materialTypes.find(mt => mt.id === mat?.material_type_id);
    const label = typeMatch?.name || mat?.material_type_id || 'this material';
    setDeleteConfirm({ type: 'material', index, label });
  };

  const executeDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteSubmitting(true);
    try {
      if (deleteConfirm.type === 'yard') {
        const yard = deleteConfirm.yard;
        await api.delete(`/material-yards/${yard.id}`);
        toast.success('Yard deleted');
        fetchYards();
        if (selectedYard?.id === yard.id) {
          setDrawerOpen(false);
          setSelectedYard(null);
        }
      } else if (deleteConfirm.type === 'material') {
        const updatedMaterials = selectedYard.materials.filter((_, i) => i !== deleteConfirm.index);
        await api.put(`/material-yards/${selectedYard.id}`, { materials: updatedMaterials });
        toast.success('Material removed');
        const response = await api.get(`/material-yards/${selectedYard.id}`);
        setSelectedYard(response.data);
        fetchYards();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteSubmitting(false);
      setDeleteConfirm(null);
    }
  };

  const toggleActive = async (yard) => {
    try {
      await api.put(`/material-yards/${yard.id}`, { is_active: !yard.is_active });
      toast.success(`Yard ${yard.is_active ? 'deactivated' : 'activated'}`);
      fetchYards();
      if (selectedYard?.id === yard.id) {
        setSelectedYard({ ...selectedYard, is_active: !yard.is_active });
      }
    } catch (error) {
      toast.error('Failed to update yard');
    }
  };

  const openYardDrawer = (yard) => {
    setSelectedYard(yard);
    setDrawerOpen(true);
  };

  // Material pricing functions
  const resetMaterialForm = () => {
    setMaterialForm({
      material_type_id: '',
      unit: 'tonne',
      charge_rate: '',
      cost_rate: '',
      buyback_rate: '',
      subbie_rate: '',
      account_id: ''
    });
    setEditingMaterial(null);
  };

  const handleOpenMaterialDialog = (material = null, index = null) => {
    if (material) {
      setEditingMaterial({ ...material, index });
      setMaterialForm({
        material_type_id: material.material_type_id,
        unit: material.unit,
        charge_rate: material.charge_rate?.toString() || '',
        cost_rate: material.cost_rate?.toString() || '',
        buyback_rate: material.buyback_rate?.toString() || '',
        subbie_rate: material.subbie_rate?.toString() || '',
        account_id: material.account_id || ''
      });
    } else {
      resetMaterialForm();
    }
    setMaterialDialogOpen(true);
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    
    const materialData = {
      material_type_id: materialForm.material_type_id,
      unit: materialForm.unit,
      charge_rate: parseFloat(materialForm.charge_rate),
      cost_rate: materialForm.cost_rate ? parseFloat(materialForm.cost_rate) : null,
      buyback_rate: materialForm.buyback_rate ? parseFloat(materialForm.buyback_rate) : null,
      subbie_rate: materialForm.subbie_rate ? parseFloat(materialForm.subbie_rate) : null,
      account_id: materialForm.account_id?.trim() || null
    };

    let updatedMaterials;
    if (editingMaterial !== null && editingMaterial.index !== undefined) {
      // Update existing material - check for duplicates excluding current item
      const exists = selectedYard.materials.some(
        (m, idx) => idx !== editingMaterial.index && 
          m.material_type_id === materialData.material_type_id && 
          m.unit === materialData.unit
      );
      if (exists) {
        toast.error('This material type + unit combination already exists');
        return;
      }
      updatedMaterials = [...selectedYard.materials];
      updatedMaterials[editingMaterial.index] = materialData;
    } else {
      // Check if material type + unit combo already exists
      const exists = selectedYard.materials.some(
        m => m.material_type_id === materialData.material_type_id && m.unit === materialData.unit
      );
      if (exists) {
        toast.error('This material type + unit combination already exists');
        return;
      }
      // Add new material
      updatedMaterials = [...(selectedYard.materials || []), materialData];
    }

    try {
      await api.put(`/material-yards/${selectedYard.id}`, { materials: updatedMaterials });
      toast.success(editingMaterial ? 'Material pricing updated' : 'Material added');
      setMaterialDialogOpen(false);
      resetMaterialForm();
      
      // Refresh the yard data
      const response = await api.get(`/material-yards/${selectedYard.id}`);
      setSelectedYard(response.data);
      fetchYards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update materials');
    }
  };

  const handleDeleteMaterial = async (index) => {
    handleDeleteMaterialConfirm(index);
  };

  const getMaterialTypeName = (id) => {
    const type = materialTypes.find(t => t.id === id);
    return type?.name || 'Unknown';
  };

  const getTypeLabel = (type) => {
    const labels = {
      tip: 'Tip',
      quarry: 'Quarry',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      tip: 'bg-orange-500/20 text-orange-400 hover:bg-orange-600/30 hover:text-orange-300',
      quarry: 'bg-blue-500/20 text-blue-400 hover:bg-blue-600/30 hover:text-blue-300',
      other: 'bg-slate-500/20 text-slate-400 hover:bg-slate-600/30 hover:text-slate-300'
    };
    return colors[type] || 'bg-slate-500/20 text-slate-400';
  };

  const formatAddress = (yard) => {
    const parts = [yard.street_address, yard.suburb, yard.state, yard.postcode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const filteredYards = yards.filter(yard => {
    const fullAddress = formatAddress(yard) || '';
    const matchesSearch = yard.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fullAddress.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || yard.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded" />;
  }

  return (
    <div className="space-y-6" data-testid="tips-materials-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-3">
            <Mountain className="h-8 w-8 text-[#ffd108]" />
            Tips & Materials
          </h1>
          <p className="text-muted-foreground">Manage tips, quarries, and material pricing</p>
        </div>
        {isAdmin && (
          <Button className="btn-volt" onClick={() => handleOpenDialog()} data-testid="add-location-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Location
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="card-industrial">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="yard-search"
                placeholder="Search by name or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="tip">Tips</SelectItem>
                <SelectItem value="quarry">Quarries</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Yards List */}
      <Card className="card-industrial">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Materials</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[70px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredYards.map(yard => (
                <TableRow 
                  key={yard.id} 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => openYardDrawer(yard)}
                >
                  <TableCell className="font-medium">{yard.name}</TableCell>
                  <TableCell>
                    <Badge className={getTypeColor(yard.type)}>
                      {getTypeLabel(yard.type)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatAddress(yard) || '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {yard.materials?.length || 0} materials
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={yard.is_active ? "default" : "secondary"}
                      className={yard.is_active ? "bg-green-500/20 text-green-400 hover:bg-green-600/30 hover:text-green-300" : ""}
                    >
                      {yard.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openYardDrawer(yard)}>
                          <ChevronRight className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {isAdmin && (
                          <>
                            <DropdownMenuItem onClick={() => handleOpenDialog(yard)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleActive(yard)}>
                              {yard.is_active ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleDelete(yard)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredYards.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    {searchTerm || typeFilter !== 'all' 
                      ? 'No matching locations found' 
                      : 'No tips or quarries configured. Add your first one!'
                    }
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Yard Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingYard ? 'Edit Location' : 'Add Location'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2" data-testid="location-form">
            {/* Google Places Lookup */}
            <div>
              <Label>Search Location</Label>
              <GooglePlacesAutocomplete
                country={tenantCountry}
                onPlaceSelect={(place) => {
                  if (place) {
                    setForm(prev => ({
                      ...prev,
                      name: place.name || prev.name,
                      street_address: place.street_address || '',
                      suburb: place.suburb || '',
                      postcode: place.postcode || '',
                      state: place.state || ''
                    }));
                  }
                }}
                placeholder="Search for a tip, quarry, or location..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Search to auto-fill name and address details
              </p>
            </div>

            <div className="border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="yard-name">Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="yard-name"
                    value={form.name}
                    onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., ABC Quarry, City Tip Site"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="yard-type">Type <span className="text-red-500">*</span></Label>
                  <Select 
                    value={form.type} 
                    onValueChange={(v) => setForm(prev => ({ ...prev, type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tip">Tip</SelectItem>
                      <SelectItem value="quarry">Quarry</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    id="yard-active"
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label htmlFor="yard-active">Active</Label>
                </div>
              </div>
            </div>

            {/* Address Fields */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium text-muted-foreground">Address Details</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div className="col-span-2">
                  <Label htmlFor="yard-street">Street Address</Label>
                  <Input
                    id="yard-street"
                    value={form.street_address}
                    onChange={(e) => setForm(prev => ({ ...prev, street_address: e.target.value }))}
                    placeholder="123 Main Street"
                  />
                </div>
                <div>
                  <Label htmlFor="yard-suburb">Suburb</Label>
                  <Input
                    id="yard-suburb"
                    value={form.suburb}
                    onChange={(e) => setForm(prev => ({ ...prev, suburb: e.target.value }))}
                    placeholder="Suburb"
                  />
                </div>
                <div>
                  <Label htmlFor="yard-postcode">Postcode</Label>
                  <Input
                    id="yard-postcode"
                    value={form.postcode}
                    onChange={(e) => setForm(prev => ({ ...prev, postcode: e.target.value }))}
                    placeholder="2000"
                  />
                </div>
                <div>
                  <Label htmlFor="yard-state">State</Label>
                  <Select 
                    value={form.state} 
                    onValueChange={(v) => setForm(prev => ({ ...prev, state: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={countryMeta?.stateLabel || 'Select state'} />
                    </SelectTrigger>
                    <SelectContent>
                      {statesList.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium text-muted-foreground">Contact Details</Label>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <Label htmlFor="yard-contact">Contact Name</Label>
                  <Input
                    id="yard-contact"
                    value={form.contact_name}
                    onChange={(e) => setForm(prev => ({ ...prev, contact_name: e.target.value }))}
                    placeholder="Contact person"
                  />
                </div>
                <div>
                  <Label htmlFor="yard-phone">Contact Phone</Label>
                  <Input
                    id="yard-phone"
                    value={form.contact_phone}
                    onChange={(e) => setForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="Phone number"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="yard-notes">Notes</Label>
                  <Textarea
                    id="yard-notes"
                    value={form.notes}
                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Operating hours, special instructions, etc."
                    rows={3}
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-volt">
                {editingYard ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Yard Detail Drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-[700px] sm:max-w-[700px] overflow-y-auto">
          {selectedYard && (
            <>
              <SheetHeader className="pb-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mountain className="h-6 w-6 text-[#ffd108]" />
                    <div>
                      <SheetTitle className="font-heading text-xl flex items-center gap-2">
                        {selectedYard.name}
                        <Badge className={getTypeColor(selectedYard.type)}>
                          {getTypeLabel(selectedYard.type)}
                        </Badge>
                      </SheetTitle>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setDrawerOpen(false);
                        handleOpenDialog(selectedYard);
                      }}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
              </SheetHeader>

              <div className="py-6 space-y-6">
                {/* Contact Info */}
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                    Contact Information
                  </h3>
                  {formatAddress(selectedYard) && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div className="text-sm">
                        {selectedYard.street_address && <div>{selectedYard.street_address}</div>}
                        <div>
                          {[selectedYard.suburb, selectedYard.state, selectedYard.postcode].filter(Boolean).join(' ')}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedYard.contact_name && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Contact:</span>
                      <span>{selectedYard.contact_name}</span>
                    </div>
                  )}
                  {selectedYard.contact_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedYard.contact_phone}</span>
                    </div>
                  )}
                  {selectedYard.notes && (
                    <div className="p-3 bg-muted/50 rounded-md text-sm">
                      {selectedYard.notes}
                    </div>
                  )}
                  {!formatAddress(selectedYard) && !selectedYard.contact_name && !selectedYard.contact_phone && !selectedYard.notes && (
                    <p className="text-sm text-muted-foreground">No contact information provided</p>
                  )}
                </div>

                {/* Materials & Pricing */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                      Materials & Pricing
                    </h3>
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenMaterialDialog()}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Material
                      </Button>
                    )}
                  </div>
                  
                  {selectedYard.materials && selectedYard.materials.length > 0 ? (
                    <div className="space-y-3">
                      {selectedYard.materials.map((mat, index) => (
                        <div 
                          key={`tip-${index}`}
                          className="p-4 bg-muted/30 rounded-lg border"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <Package className="h-5 w-5 text-[#ffd108]" />
                              <div>
                                <p className="font-medium">{mat.material_name || getMaterialTypeName(mat.material_type_id)}</p>
                                <p className="text-xs text-muted-foreground">per {mat.unit}</p>
                              </div>
                            </div>
                            {isAdmin && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleOpenMaterialDialog(mat, index)}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit Pricing
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-destructive"
                                    onClick={() => handleDeleteMaterial(index)}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Remove
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex justify-between p-2 bg-background/50 rounded">
                              <span className="text-muted-foreground">Charge</span>
                              <span className="font-semibold text-[#ffd108]">${mat.charge_rate?.toFixed(2) || '0.00'}</span>
                            </div>
                            <div className="flex justify-between p-2 bg-background/50 rounded">
                              <span className="text-muted-foreground">Cost</span>
                              <span className="font-medium">{mat.cost_rate ? `$${mat.cost_rate.toFixed(2)}` : '-'}</span>
                            </div>
                            <div className="flex justify-between p-2 bg-background/50 rounded">
                              <span className="text-muted-foreground">Buy Back</span>
                              <span className="font-medium">{mat.buyback_rate ? `$${mat.buyback_rate.toFixed(2)}` : '-'}</span>
                            </div>
                            <div className="flex justify-between p-2 bg-background/50 rounded">
                              <span className="text-muted-foreground">Subbie Rate</span>
                              <span className="font-medium">{mat.subbie_rate ? `$${mat.subbie_rate.toFixed(2)}` : '-'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No materials configured</p>
                      {isAdmin && (
                        <Button 
                          variant="link" 
                          className="mt-2"
                          onClick={() => handleOpenMaterialDialog()}
                        >
                          Add your first material
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Add/Edit Material Dialog */}
      <Dialog open={materialDialogOpen} onOpenChange={setMaterialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingMaterial ? 'Edit Material Pricing' : 'Add Material'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMaterialSubmit} className="space-y-4">
            <div>
              <Label htmlFor="mat-type">Material Type <span className="text-red-500">*</span></Label>
              <Select 
                value={materialForm.material_type_id} 
                onValueChange={(v) => setMaterialForm(prev => ({ ...prev, material_type_id: v }))}
                disabled={editingMaterial !== null}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select material type" />
                </SelectTrigger>
                <SelectContent>
                  {materialTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {materialTypes.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  No material types configured. Add them in Data Management first.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="mat-unit">Unit <span className="text-red-500">*</span></Label>
              <Select 
                value={materialForm.unit} 
                onValueChange={(v) => setMaterialForm(prev => ({ ...prev, unit: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tonne">Tonne</SelectItem>
                  <SelectItem value="m3">Cubic Metre (m³)</SelectItem>
                  <SelectItem value="load">Load</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="border-t pt-4 mt-2">
              <p className="text-sm font-medium mb-3">Rates (per {materialForm.unit})</p>
              
              <div className="space-y-3">
                <div>
                  <Label htmlFor="mat-charge">Charge (Customer Invoice) <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="mat-charge"
                      type="number"
                      step="0.01"
                      min="0"
                      value={materialForm.charge_rate}
                      onChange={(e) => setMaterialForm(prev => ({ ...prev, charge_rate: e.target.value }))}
                      className="pl-9"
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">What you charge the customer</p>
                </div>
                
                <div>
                  <Label htmlFor="mat-cost">Cost</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="mat-cost"
                      type="number"
                      step="0.01"
                      min="0"
                      value={materialForm.cost_rate}
                      onChange={(e) => setMaterialForm(prev => ({ ...prev, cost_rate: e.target.value }))}
                      className="pl-9"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">What the tip/quarry charges you</p>
                </div>
                
                <div>
                  <Label htmlFor="mat-buyback">Buy Back</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="mat-buyback"
                      type="number"
                      step="0.01"
                      min="0"
                      value={materialForm.buyback_rate}
                      onChange={(e) => setMaterialForm(prev => ({ ...prev, buyback_rate: e.target.value }))}
                      className="pl-9"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">What the tip/quarry pays you for the material</p>
                </div>
                
                <div>
                  <Label htmlFor="mat-subbie">Subbie Rate</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="mat-subbie"
                      type="number"
                      step="0.01"
                      min="0"
                      value={materialForm.subbie_rate}
                      onChange={(e) => setMaterialForm(prev => ({ ...prev, subbie_rate: e.target.value }))}
                      className="pl-9"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">What you pay a subcontractor</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <Label htmlFor="mat-account-id">Account ID (override)</Label>
              <Input
                id="mat-account-id"
                data-testid="material-yard-account-id-input"
                value={materialForm.account_id}
                onChange={(e) => setMaterialForm(prev => ({ ...prev, account_id: e.target.value }))}
                placeholder="Leave blank to use the material's default"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Optional. Overrides the material type's Account ID for this tip/quarry on invoice export.
              </p>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMaterialDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="btn-volt"
                disabled={!materialForm.material_type_id || !materialForm.charge_rate}
              >
                {editingMaterial ? 'Save Changes' : 'Add Material'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {deleteConfirm?.type === 'yard' ? 'Delete Yard' : 'Remove Material'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deleteConfirm?.type === 'yard'
              ? <>Are you sure you want to delete <span className="font-medium text-foreground">"{deleteConfirm?.label}"</span>? This action cannot be undone.</>
              : <>Remove <span className="font-medium text-foreground">{deleteConfirm?.label}</span> from this yard?</>}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={deleteSubmitting} data-testid="delete-confirm-cancel">
              Cancel
            </Button>
            <Button variant="destructive" onClick={executeDelete} disabled={deleteSubmitting} data-testid="delete-confirm-submit">
              {deleteSubmitting ? 'Deleting...' : deleteConfirm?.type === 'yard' ? 'Delete' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TipsMaterials;
